import { format } from 'date-fns';

/**
 * Date utilities that avoid timezone shifts for date-only fields.
 * When the backend returns "2025-09-22T00:00:00.000Z", using new Date() + toLocaleDateString
 * can show 9/21 in timezones west of UTC. These helpers extract the date part without conversion.
 */

/**
 * Parse a time entry timestamp from the API (stored in UTC).
 * If the value has no timezone suffix (e.g. "2026-03-03 05:00:00" from PG), treat as UTC
 * so that when we format in local we get the correct local time.
 * Returns a Date or null.
 */
export function parseTimeEntryDate(v) {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  if (!s) return null;
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  const iso = hasTz ? s : s.replace(/\s+/, 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Parse API timestamps consistently for local display.
 * Rules:
 * - Date-only (YYYY-MM-DD): parse as local date.
 * - Datetime with explicit timezone (Z / +HH:mm / +HHmm / +HH): parse as given timezone.
 * - Datetime with no timezone: assume UTC (common backend/DB behavior), then display in local.
 */
export function parseApiTimestamp(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const monthIdx = Number(dateOnly[2]) - 1;
    const day = Number(dateOnly[3]);
    const d = new Date(year, monthIdx, day, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  let normalized = raw.replace(' ', 'T');
  normalized = normalized.replace(/\.(\d{3})\d+/, '.$1'); // trim microseconds
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'); // +0000 -> +00:00
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00'); // +00 -> +00:00

  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized);
  if (!hasTimezone) normalized = `${normalized}Z`;

  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a time entry timestamp for display (UTC → local).
 * Use for clock_in, clock_out in time entry lists and dialogs.
 */
export function formatTimeEntry(v, pattern = 'MM/dd/yyyy hh:mm a') {
  const d = parseTimeEntryDate(v);
  if (!d) return '—';
  return format(d, pattern);
}

/**
 * Format a time entry timestamp for use in <input type="datetime-local"> (local time).
 */
export function toDatetimeLocalForEntry(v) {
  const d = parseTimeEntryDate(v);
  if (!d) return '';
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Parse a YYYY-MM-DD string as local date (no UTC shift).
 * new Date("2026-03-01") is UTC midnight → previous day in US; this keeps March 1 in local time.
 */
export function parseLocalDate(ymdString) {
  if (!ymdString || typeof ymdString !== 'string') return null;
  const part = ymdString.trim().split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return null;
  const [y, m, d] = part.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Today's calendar date as YYYY-MM-DD in the user's local timezone.
 * Prefer over `new Date().toISOString().slice(0, 10)` for form defaults (ISO uses UTC).
 */
export function formatLocalTodayYmd() {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Numeric sort key for date-only API values (Postgres DATE often serialized as midnight UTC).
 * Uses calendar YYYY-MM-DD so e.g. 2026-03-30Z is ordered as March 30 everywhere, not the prior local day.
 */
export function sortTimestampFromDateOnly(value) {
  if (value == null || value === '') return 0;
  const part = String(value).trim().split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
    return parseLocalDate(part)?.getTime() ?? 0;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Extract YYYY-MM-DD from a date value for use in <input type="date">.
 * Avoids timezone shift: "2025-09-22T00:00:00.000Z" -> "2025-09-22" (not 9/21 in Central).
 */
export function formatDateForInput(dateValue) {
  if (!dateValue) return '';
  const s = String(dateValue);
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  return '';
}

/**
 * Format date for display (MM/DD/YYYY) without timezone shift.
 */
export function formatDateDisplay(dateValue) {
  if (!dateValue) return '—';
  const s = String(dateValue);
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1]}`;
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * Format a date value for display with a date-fns pattern, without timezone shift for date-only values.
 * Use for quote_date, expires_on, etc. so "2026-03-16" shows as Mon 3/16 in all timezones, not Sun 3/15.
 * - If value is yyyy-MM-dd (or ISO with that date part), parses as local date and formats.
 * - Otherwise treats as timestamp (e.g. created_at) and formats in local time.
 */
export function formatDateOnly(dateValue, pattern = 'MM/dd/yyyy') {
  if (dateValue == null || dateValue === '') return '';
  const s = String(dateValue).trim();
  const part = s.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
    const d = parseLocalDate(part);
    return d ? format(d, pattern) : '';
  }
  try {
    const d = new Date(dateValue);
    return isNaN(d.getTime()) ? '' : format(d, pattern);
  } catch {
    return '';
  }
}
